import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../context/UserContext';
import SearchableSelect from '../components/SearchableSelect';
import { parseMultiColumnPaste } from '../utils/multiColumnList';
import { buildMatchFieldOptions, MATCH_TYPES } from '../utils/targetingListFields';
import { buildStatusGroupMap, buildProgramOptionGroups } from '../utils/statusGroups';
import ClientListImportWizard from '../components/ClientListImportWizard';
import SetColumnEditor from '../components/SetColumnEditor';

const API = process.env.REACT_APP_API_URL;

// Built-in multi-column list types (see backend TargetingList.columns) — the
// full ordered column set, columns[0] is always the row's primary/`.value`
// field. A custom list type added via "Manage List Types" stays single-column
// (no entry here) unless a future round teaches this page to define arbitrary
// columns at creation time.
const MULTI_COLUMN_LIST_TYPES = {
  'Title Keywords': ['keyword', 'category', 'match_type'],
  'Carryover Domain Counts': ['domain', 'count'],
};

// "Set Check" (2026-09-21) — the "arbitrary columns at creation time" gap the
// comment above used to point to. Unlike the two built-ins, its columns are
// PM-defined per-list (name + target Lead field + own match strategy), not a
// fixed set per type, so it's handled separately via SetColumnEditor rather
// than being added to MULTI_COLUMN_LIST_TYPES above.
const SET_CHECK_LIST_TYPE = 'Set Check';

// Deterministic color from type label so each type always gets the same color
const COLOR_PALETTE = [
  '#3498db', '#9b59b6', '#e67e22', '#1abc9c',
  '#e74c3c', '#34495e', '#7f8c8d', '#2ecc71',
  '#e91e63', '#795548', '#607d8b', '#f39c12',
];
function typeColor(label = '') {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = ((h << 5) - h + label.charCodeAt(i)) | 0;
  return COLOR_PALETTE[Math.abs(h) % COLOR_PALETTE.length];
}

const btn = (color) => ({
  padding: '6px 14px', background: color, color: 'white', border: 'none',
  borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap',
});
const inputStyle = {
  padding: '7px 10px', borderRadius: '4px', border: '1px solid #ccc',
  fontSize: '13px', boxSizing: 'border-box',
};
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '3px', display: 'block', fontWeight: '500' };

function TypeBadge({ type }) {
  return (
    <span style={{ background: typeColor(type), color: 'white', borderRadius: '4px', padding: '2px 7px', fontSize: '11px' }}>
      {type || '—'}
    </span>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '1px solid #eee', marginTop: '16px' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '12px 0', fontSize: '13px', fontWeight: '600', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left' }}>
        <span style={{ fontSize: '11px' }}>{open ? '▼' : '▶'}</span>{title}
      </button>
      {open && <div style={{ paddingBottom: '12px' }}>{children}</div>}
    </div>
  );
}

export default function TargetingLists() {
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const location = useLocation();
  // QA Rules' "↗" link opens this page in a new tab (window.open), which
  // can't carry React Router's in-memory `state` across — so it passes
  // `?selectListId=` in the URL instead. Support both: `state` for any
  // same-tab navigate() caller, the query param for a fresh tab/window.
  const selectListIdParam = new URLSearchParams(location.search).get('selectListId');
  const pendingSelectId = useRef(
    location.state?.selectListId || (selectListIdParam ? Number(selectListIdParam) : null)
  );

  // ── Left panel ──────────────────────────────────────────────────────────
  const [lists, setLists] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterComponent, setFilterComponent] = useState('');
  const [filterGoal, setFilterGoal] = useState('');
  const [filterUnassigned, setFilterUnassigned] = useState(false);
  const [filterNeedsReview, setFilterNeedsReview] = useState(false);
  const [filterProgramOptions, setFilterProgramOptions] = useState([]);
  const [filterComponentOptions, setFilterComponentOptions] = useState([]);
  const [filterGoalOptions, setFilterGoalOptions] = useState([]);

  // ── Right panel ─────────────────────────────────────────────────────────
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);

  // Items
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemPage, setItemPage] = useState(1);
  const [itemSearch, setItemSearch] = useState('');
  const [itemSearchInput, setItemSearchInput] = useState('');
  const [addText, setAddText] = useState('');
  const [uploadMode, setUploadMode] = useState('append');
  const [savingItems, setSavingItems] = useState(false);
  const fileRef = useRef(null);

  // Assignments
  const [assignments, setAssignments] = useState([]);
  const [assignProgram, setAssignProgram] = useState('');
  const [assignComponentOptions, setAssignComponentOptions] = useState([]);
  const [assignComponent, setAssignComponent] = useState('');
  const [assignGoalOptions, setAssignGoalOptions] = useState([]);
  const [assignGoalIds, setAssignGoalIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Inline edit
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Clone
  const [showClone, setShowClone] = useState(false);
  const [cloneName, setCloneName] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ list_name: '', list_type: '', match_field: '', match_type: 'exact', client_id: '', is_global: false });
  // Set Check's columns are PM-defined per-list, not fixed per-type like
  // MULTI_COLUMN_LIST_TYPES below — [{name, lead_field, match_type}].
  const [newSetColumns, setNewSetColumns] = useState([{ name: '', lead_field: '', match_type: 'exact' }]);

  // Client List Processor wizard
  const [showImportWizard, setShowImportWizard] = useState(false);

  // Reference data
  const [listTypes, setListTypes] = useState([]);   // from lookup_values
  const [clients, setClients] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]); // unfiltered for assignment panel
  const [assignProgramOptions, setAssignProgramOptions] = useState([]);
  // Closed programs nest into a collapsible section on every Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const filterProgramOptionGroups = buildProgramOptionGroups(filterProgramOptions, programStatusGroupMap);
  const assignProgramOptionGroups = buildProgramOptionGroups(assignProgramOptions, programStatusGroupMap);
  const [allLeadFields, setAllLeadFields] = useState([]); // [{key,label,group}] from GET /leads/export-fields — full match-field search list

  // List type management (admin only)
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);

  const PER_PAGE = 50;

  // ── Load reference data ─────────────────────────────────────────────────
  const fetchListTypes = useCallback(() => {
    axios.get(`${API}/lookup-values/?category=targeting_list_type`)
      .then(r => setListTypes(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchListTypes();
    axios.get(`${API}/clients/`).then(r => setClients(r.data)).catch(() => {});
    axios.get(`${API}/programs/`).then(r => setAllPrograms(r.data)).catch(() => {});
    axios.get(`${API}/leads/export-fields`).then(r => setAllLeadFields(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, [fetchListTypes]);

  // Left-panel: filter program list by active client filter
  useEffect(() => {
    if (filterClient) {
      setFilterProgramOptions(allPrograms.filter(p => p.client_id === parseInt(filterClient)));
      setFilterProgram('');
      setFilterGoal('');
    } else {
      setFilterProgramOptions(allPrograms);
    }
  }, [filterClient, allPrograms]);

  // Left-panel: load components when filter program changes
  useEffect(() => {
    if (filterProgram) {
      axios.get(`${API}/components/?program_id=${filterProgram}`)
        .then(r => setFilterComponentOptions(r.data))
        .catch(() => setFilterComponentOptions([]));
    } else {
      setFilterComponentOptions([]);
      setFilterGoalOptions([]);
    }
    setFilterComponent('');
    setFilterGoal('');
  }, [filterProgram]);

  // Left-panel: load goals when filter component changes
  useEffect(() => {
    if (filterComponent) {
      axios.get(`${API}/goals/?component_id=${filterComponent}`)
        .then(r => setFilterGoalOptions(r.data))
        .catch(() => setFilterGoalOptions([]));
    } else {
      setFilterGoalOptions([]);
    }
    setFilterGoal('');
  }, [filterComponent]);

  // Assignment panel: filter programs by the active list's client
  useEffect(() => {
    const clientId = detail?.client_id;
    if (clientId) {
      setAssignProgramOptions(allPrograms.filter(p => p.client_id === clientId));
    } else {
      setAssignProgramOptions(allPrograms);
    }
  }, [detail?.client_id, allPrograms]);

  // Assignment panel: load components when program chosen
  useEffect(() => {
    if (assignProgram) {
      axios.get(`${API}/components/?program_id=${assignProgram}`)
        .then(r => setAssignComponentOptions(r.data))
        .catch(() => setAssignComponentOptions([]));
      setAssignComponent('');
      setAssignGoalOptions([]);
      setAssignGoalIds([]);
    } else {
      setAssignComponentOptions([]);
      setAssignComponent('');
      setAssignGoalOptions([]);
      setAssignGoalIds([]);
    }
  }, [assignProgram]);

  // Assignment panel: load goals when component chosen
  useEffect(() => {
    if (assignComponent) {
      axios.get(`${API}/goals/?component_id=${assignComponent}`)
        .then(r => setAssignGoalOptions(r.data))
        .catch(() => setAssignGoalOptions([]));
      setAssignGoalIds([]);
    } else {
      setAssignGoalOptions([]);
      setAssignGoalIds([]);
    }
  }, [assignComponent]);

  // ── Fetch lists ─────────────────────────────────────────────────────────
  const fetchLists = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (filterType) p.set('list_type', filterType);
    if (filterClient) p.set('client_id', filterClient);
    if (filterGoal) p.set('goal_id', filterGoal);
    else if (filterComponent) p.set('component_id', filterComponent);
    else if (filterProgram) p.set('program_id', filterProgram);
    if (filterUnassigned) p.set('unassigned', 'true');
    if (filterNeedsReview) p.set('needs_review', 'true');
    axios.get(`${API}/targeting-lists/?${p}`).then(r => setLists(r.data)).catch(() => {});
  }, [search, filterType, filterClient, filterProgram, filterComponent, filterGoal, filterUnassigned, filterNeedsReview]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // ── Fetch detail / items / assignments ──────────────────────────────────
  const fetchDetail = useCallback((id) => {
    axios.get(`${API}/targeting-lists/${id}`).then(r => setDetail(r.data)).catch(() => {});
  }, []);

  const fetchItems = useCallback((id, page, srch) => {
    const p = new URLSearchParams({ page, per_page: PER_PAGE });
    if (srch) p.set('search', srch);
    axios.get(`${API}/targeting-lists/${id}/items?${p}`)
      .then(r => { setItems(r.data.items); setItemsTotal(r.data.total); })
      .catch(() => {});
  }, []);

  const fetchAssignments = useCallback((id) => {
    axios.get(`${API}/targeting-lists/${id}/assignments`).then(r => setAssignments(r.data)).catch(() => {});
  }, []);

  const selectList = (lst) => {
    setActiveId(lst.id);
    setDetail(null);
    setItems([]);
    setItemsTotal(0);
    setItemPage(1);
    setItemSearch('');
    setItemSearchInput('');
    setAssignments([]);
    setEditField(null);
    setShowClone(false);
    setAssignProgram('');
    fetchDetail(lst.id);
    fetchAssignments(lst.id);
    // items fetched by useEffect watching [activeId, itemPage, itemSearch]
  };

  useEffect(() => {
    if (activeId) fetchItems(activeId, itemPage, itemSearch);
  }, [activeId, itemPage, itemSearch, fetchItems]);

  // Deep-link support: QA Rules "view in List Management" links navigate here with
  // { state: { selectListId } } — open that list's detail panel once it's in view.
  useEffect(() => {
    if (pendingSelectId.current && lists.some(l => l.id === pendingSelectId.current)) {
      selectList({ id: pendingSelectId.current });
      pendingSelectId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists]);

  // ── Create ──────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    const isSetCheck = newForm.list_type === SET_CHECK_LIST_TYPE;
    let columns = MULTI_COLUMN_LIST_TYPES[newForm.list_type] || undefined;
    let columnConfig;
    let matchField = newForm.match_field;
    if (isSetCheck) {
      const validCols = newSetColumns.filter(c => c.name.trim() && c.lead_field);
      if (!validCols.length) { alert('Please define at least one column with a name and a matching Lead field.'); return; }
      columns = validCols.map(c => c.name.trim());
      columnConfig = validCols;
      matchField = columns[0];   // nominal — check_set() only ever reads column_config
    } else if (!newForm.match_field) {
      alert('Please select a Match Field.');
      return;
    }
    const data = {
      ...newForm,
      match_field: matchField,
      client_id: newForm.client_id ? parseInt(newForm.client_id) : null,
      columns,
      column_config: columnConfig,
    };
    const res = await axios.post(`${API}/targeting-lists/`, data);
    setShowCreate(false);
    setNewForm({ list_name: '', list_type: listTypes[0]?.value || '', match_field: '', match_type: 'exact', client_id: '', is_global: false });
    setNewSetColumns([{ name: '', lead_field: '', match_type: 'exact' }]);
    fetchLists();
    selectList(res.data);
  };

  // ── Delete / Bulk delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this targeting list and all its items?')) return;
    await axios.delete(`${API}/targeting-lists/${id}`);
    if (activeId === id) { setActiveId(null); setDetail(null); }
    setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    fetchLists();
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} targeting list(s) and all their items?`)) return;
    await axios.post(`${API}/targeting-lists/bulk-delete`, { ids: [...selectedIds] });
    if (selectedIds.has(activeId)) { setActiveId(null); setDetail(null); }
    setSelectedIds(new Set());
    fetchLists();
  };

  // ── Inline edit ─────────────────────────────────────────────────────────
  const startEdit = (field, value) => { setEditField(field); setEditValue(value ?? ''); };

  const saveEdit = async () => {
    if (!activeId || !editField) return;
    const payload = { [editField]: editField === 'client_id' ? (editValue ? parseInt(editValue) : null) : editValue };
    const res = await axios.put(`${API}/targeting-lists/${activeId}`, payload);
    setDetail(res.data);
    setEditField(null);
    fetchLists();
  };

  // ── Items ────────────────────────────────────────────────────────────────
  const handleAddItems = async () => {
    if (!activeId || !addText.trim()) return;
    setSavingItems(true);
    const body = detail?.columns
      ? { rows: parseMultiColumnPaste(addText, detail.columns) }
      : { values: addText.split('\n').map(v => v.trim()).filter(Boolean) };
    try {
      const res = await axios.post(`${API}/targeting-lists/${activeId}/items`, body);
      setDetail(res.data);
      setAddText('');
      fetchItems(activeId, 1, itemSearch);
      setItemPage(1);
      fetchLists();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add items');
    }
    setSavingItems(false);
  };

  const handleDeleteItem = async (itemId) => {
    await axios.delete(`${API}/targeting-lists/${activeId}/items/${itemId}`);
    fetchItems(activeId, itemPage, itemSearch);
    fetchDetail(activeId);
    fetchLists();
  };

  const handleClearAll = async () => {
    if (!window.confirm('Remove all items from this list?')) return;
    await axios.delete(`${API}/targeting-lists/${activeId}/items`);
    setItems([]);
    setItemsTotal(0);
    setItemPage(1);
    fetchDetail(activeId);
    fetchLists();
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/targeting-lists/${activeId}/upload-csv?mode=${uploadMode}`, fd);
      setDetail(res.data);
      setItemPage(1);
      fetchItems(activeId, 1, itemSearch);
      fetchLists();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const handleDownload = async () => {
    const res = await axios.get(`${API}/targeting-lists/${activeId}/items/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${detail?.list_name || 'list'}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Item search debounce
  const itemSearchTimer = useRef(null);
  const handleItemSearchChange = (val) => {
    setItemSearchInput(val);
    clearTimeout(itemSearchTimer.current);
    itemSearchTimer.current = setTimeout(() => { setItemSearch(val); setItemPage(1); }, 300);
  };

  // ── Clone ────────────────────────────────────────────────────────────────
  const handleClone = async () => {
    if (!cloneName.trim()) return;
    const res = await axios.post(`${API}/targeting-lists/${activeId}/clone`, { new_name: cloneName });
    setShowClone(false);
    setCloneName('');
    fetchLists();
    selectList(res.data);
  };

  // ── Assignments ──────────────────────────────────────────────────────────
  const handleAssign = async () => {
    if (!assignProgram) return;
    setAssigning(true);
    let res;
    try {
      if (assignGoalIds.length) {
        res = await axios.post(`${API}/targeting-lists/${activeId}/assignments`, { goal_ids: assignGoalIds.map(Number) });
      } else if (assignComponent) {
        res = await axios.post(`${API}/targeting-lists/${activeId}/assign-component`, { component_id: parseInt(assignComponent) });
      } else {
        res = await axios.post(`${API}/targeting-lists/${activeId}/assign-program`, { program_id: parseInt(assignProgram) });
      }
      setAssignments(res.data);
      setAssignProgram('');
      setAssignComponent('');
      setAssignGoalIds([]);
      fetchDetail(activeId);
      fetchLists();
    } catch (err) {
      alert(err.response?.data?.detail || 'Assignment failed');
    }
    setAssigning(false);
  };

  const handleRemoveAssignment = async (assignment) => {
    // Uses the same DELETE .../unassign?level=&entity_id= endpoint QARules.js's
    // ListManagePanel calls, keyed off the entity id (program/component/goal),
    // not the legacy .../assignments/{id}?assignment_type= endpoint — that one
    // only ever accepted assignment_type=goal|component, so clicking × on a
    // program-level row (every list type has been program-assignable since
    // 2026-07-30) 422'd silently with no error surfaced, which read as "the ×
    // does nothing."
    const entityId = assignment.type === 'program' ? assignment.program_id
      : assignment.type === 'component' ? assignment.component_id
      : assignment.goal_id;
    try {
      await axios.delete(`${API}/targeting-lists/${activeId}/unassign`, {
        params: { level: assignment.type, entity_id: entityId },
      });
      fetchAssignments(activeId);
      fetchDetail(activeId);
      fetchLists();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove assignment');
    }
  };

  // ── List type management (admin) ─────────────────────────────────────────
  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    setAddingType(true);
    try {
      await axios.post(`${API}/lookup-values/`, {
        category: 'targeting_list_type',
        value: newTypeName.trim(),
        sort_order: listTypes.length * 10,
      });
      setNewTypeName('');
      fetchListTypes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add type');
    }
    setAddingType(false);
  };

  const handleDeleteType = async (id, label) => {
    if (!window.confirm(`Remove list type "${label}"? Existing lists of this type are not affected.`)) return;
    await axios.delete(`${API}/lookup-values/${id}`);
    fetchListTypes();
  };

  // ── Checkbox helpers ────────────────────────────────────────────────────
  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === lists.length && lists.length > 0 ? new Set() : new Set(lists.map(l => l.id)));
  };

  const pages = Math.max(1, Math.ceil(itemsTotal / PER_PAGE));
  const activeList = lists.find(l => l.id === activeId);

  // Assign button label
  const assignLabel = () => {
    if (assigning) return 'Assigning…';
    if (assignGoalIds.length) return `Assign (${assignGoalIds.length} goal${assignGoalIds.length > 1 ? 's' : ''})`;
    if (assignComponent) return 'Assign All Goals in Component';
    if (assignProgram) return 'Assign All Goals in Program';
    return 'Assign';
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>List Management</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowCreate(true)} style={btn('#2c3e50')}>+ New List</button>
          <button onClick={() => setShowImportWizard(true)} style={btn('#8e44ad')}>+ Import Client List</button>
        </div>
      </div>

      {showImportWizard && (
        <ClientListImportWizard
          onClose={() => setShowImportWizard(false)}
          onImported={() => { setShowImportWizard(false); fetchLists(); }}
        />
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px' }}>New Targeting List</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>List Name *</label>
              <input value={newForm.list_name} onChange={e => setNewForm({ ...newForm, list_name: e.target.value })} required style={{ ...inputStyle, width: '100%' }} />
            </div>
            <div>
              <label style={labelStyle}>List Type *</label>
              <select value={newForm.list_type} onChange={e => setNewForm({ ...newForm, list_type: e.target.value })} required style={{ ...inputStyle, width: '100%' }}>
                <option value="">Select type…</option>
                {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
              </select>
            </div>
            {newForm.list_type !== SET_CHECK_LIST_TYPE && (
              <div>
                <label style={labelStyle}>Match Field * <span style={{ fontWeight: 400, color: '#888' }}>(lead column checked against)</span></label>
                <SearchableSelect
                  options={buildMatchFieldOptions(allLeadFields, newForm.match_field)}
                  value={newForm.match_field}
                  onChange={v => setNewForm({ ...newForm, match_field: v })}
                  placeholder="Search fields…"
                />
              </div>
            )}
            {newForm.list_type !== SET_CHECK_LIST_TYPE && (
              <div>
                <label style={labelStyle}>Match Type *</label>
                <select value={newForm.match_type} onChange={e => setNewForm({ ...newForm, match_type: e.target.value })} required style={{ ...inputStyle, width: '100%' }}>
                  {Object.entries(MATCH_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={labelStyle}>Client (optional)</label>
              <select value={newForm.client_id} onChange={e => setNewForm({ ...newForm, client_id: e.target.value })} style={{ ...inputStyle, width: '100%' }}>
                <option value="">None</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {newForm.list_type === SET_CHECK_LIST_TYPE ? (
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Columns * <span style={{ fontWeight: 400, color: '#888' }}>(a lead matches an entry only when every column matches)</span></label>
              <SetColumnEditor columns={newSetColumns} onChange={setNewSetColumns} allLeadFields={allLeadFields} />
            </div>
          ) : MULTI_COLUMN_LIST_TYPES[newForm.list_type] && (
            <div style={{ fontSize: '12px', color: '#7f8c8d', background: '#eef4ff', border: '1px solid #c0d4f5', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px' }}>
              This list type holds a row per item with columns: <strong>{MULTI_COLUMN_LIST_TYPES[newForm.list_type].join(', ')}</strong> — you'll paste multi-column rows instead of one value per line.
            </div>
          )}
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', marginBottom: '16px', cursor: 'pointer' }}>
              <input type="checkbox" checked={newForm.is_global} onChange={e => setNewForm({ ...newForm, is_global: e.target.checked })} />
              🌐 Global — applies to every lead everywhere, regardless of program assignment (same mechanism as "Global DemandArc" suppression / "Global DemandArc DNC")
            </label>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={btn('#27ae60')}>Create</button>
            <button type="button" onClick={() => setShowCreate(false)} style={btn('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* ── Left panel ── */}
        <div style={{ width: '320px', flexShrink: 0 }}>

          {/* Filters */}
          <div style={{ background: '#f8f9fa', border: '1px solid #e8ecef', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
            <input placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, width: '100%', marginBottom: '8px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
                <option value="">All Types</option>
                {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
              </select>
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={inputStyle}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <SearchableSelect value={filterProgram} onChange={setFilterProgram}
                placeholder="All Programs"
                options={filterProgramOptionGroups.options}
                closedOptions={filterProgramOptionGroups.closedOptions} />
            </div>
            {filterProgram && filterComponentOptions.length > 0 && (
              <div style={{ marginBottom: '6px' }}>
                <select value={filterComponent} onChange={e => setFilterComponent(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                  <option value="">All Components</option>
                  {filterComponentOptions.map(c => <option key={c.id} value={c.id}>{c.component_name}</option>)}
                </select>
              </div>
            )}
            {filterComponent && filterGoalOptions.length > 0 && (
              <div style={{ marginBottom: '6px' }}>
                <select value={filterGoal} onChange={e => setFilterGoal(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                  <option value="">All Goals in Component</option>
                  {filterGoalOptions.map(g => <option key={g.id} value={g.id}>{g.goal_name}</option>)}
                </select>
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', cursor: 'pointer', marginBottom: '4px' }}>
              <input type="checkbox" checked={filterUnassigned} onChange={e => setFilterUnassigned(e.target.checked)} />
              Unassigned lists only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#e67e22', cursor: 'pointer', fontWeight: filterNeedsReview ? '600' : '400' }}>
              <input type="checkbox" checked={filterNeedsReview} onChange={e => setFilterNeedsReview(e.target.checked)} />
              Needs review only
            </label>
          </div>

          {/* Bulk bar */}
          {lists.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', padding: '4px 2px' }}>
              <input type="checkbox" checked={selectedIds.size === lists.length && lists.length > 0} onChange={toggleSelectAll} title="Select all" />
              <span style={{ fontSize: '12px', color: '#777', flex: 1 }}>
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${lists.length} lists`}
              </span>
              {selectedIds.size > 0 && (
                <button onClick={handleBulkDelete} style={{ ...btn('#e74c3c'), fontSize: '12px', padding: '4px 10px' }}>
                  Delete ({selectedIds.size})
                </button>
              )}
            </div>
          )}

          {/* List */}
          <div style={{ background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', overflow: 'hidden' }}>
            {lists.length === 0 && <div style={{ padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>No lists found.</div>}
            {lists.map(l => (
              <div key={l.id} onClick={() => selectList(l)}
                style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: activeId === l.id ? '#ebf5fb' : 'white', display: 'flex', alignItems: 'flex-start', gap: '8px' }}
                onMouseEnter={e => { if (activeId !== l.id) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { if (activeId !== l.id) e.currentTarget.style.background = 'white'; }}
              >
                <input type="checkbox" checked={selectedIds.has(l.id)} onChange={e => toggleSelect(e, l.id)} onClick={e => e.stopPropagation()} style={{ marginTop: '3px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{l.list_name}</span>
                    {l.needs_review && <span style={{ background: '#e67e22', color: 'white', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>Review</span>}
                    {l.is_global && <span title="Applies to every lead everywhere, regardless of program assignment" style={{ background: '#8e44ad', color: 'white', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>🌐 Global</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <TypeBadge type={l.list_type} />
                    <span style={{ fontSize: '11px', color: '#aaa' }}>{l.item_count.toLocaleString()} items</span>
                    {l.assignment_count > 0
                      ? <span style={{ fontSize: '11px', color: '#27ae60' }}>{l.assignment_count} assigned</span>
                      : <span style={{ fontSize: '11px', color: '#e67e22' }}>unassigned</span>}
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); handleDelete(l.id); }} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '16px', padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!activeId && (
            <div style={{ padding: '60px', textAlign: 'center', color: '#aaa', background: 'white', border: '1px solid #e8ecef', borderRadius: '8px' }}>
              Select a list to view and manage it
            </div>
          )}

          {activeId && (
            <div style={{ background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', padding: '20px' }}>

              {/* ── Needs review banner ── */}
              {detail?.needs_review && (
                <div style={{ background: '#fef5e7', border: '1px solid #f39c12', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#c0392b' }}>⚠ Needs Review</span>
                    <span style={{ fontSize: '12px', color: '#7d6608', marginLeft: '8px' }}>Auto-created during PDF import — verify items, match field, and type before use.</span>
                  </div>
                  <button
                    onClick={async () => {
                      const res = await axios.put(`${API}/targeting-lists/${activeId}`, { needs_review: false });
                      setDetail(res.data);
                      fetchLists();
                    }}
                    style={{ padding: '5px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>
                    Mark as Reviewed
                  </button>
                </div>
              )}

              {/* ── Info ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ flex: 1 }}>

                    {/* Name */}
                    {editField === 'list_name' ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
                        <input value={editValue} onChange={e => setEditValue(e.target.value)} style={{ ...inputStyle, fontSize: '15px', fontWeight: '600' }} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditField(null); }} />
                        <button onClick={saveEdit} style={btn('#27ae60')}>Save</button>
                        <button onClick={() => setEditField(null)} style={btn('#95a5a6')}>Cancel</button>
                      </div>
                    ) : (
                      <h3 style={{ margin: '0 0 10px', color: '#2c3e50', cursor: 'pointer' }} title="Click to edit"
                        onClick={() => startEdit('list_name', detail?.list_name || activeList?.list_name)}>
                        {detail?.list_name || activeList?.list_name}
                        <span style={{ fontSize: '12px', color: '#bbb', marginLeft: '8px', fontWeight: 400 }}>✎</span>
                      </h3>
                    )}

                    {/* Metadata row */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: '#555' }}>

                      {/* Type */}
                      <div>
                        <span style={{ color: '#888' }}>Type: </span>
                        {editField === 'list_type' ? (
                          <span style={{ display: 'inline-flex', gap: '4px' }}>
                            <select value={editValue} onChange={e => setEditValue(e.target.value)} style={{ ...inputStyle, padding: '2px 6px', fontSize: '12px' }}>
                              {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
                            </select>
                            <button onClick={saveEdit} style={{ ...btn('#27ae60'), padding: '2px 8px', fontSize: '12px' }}>✓</button>
                            <button onClick={() => setEditField(null)} style={{ ...btn('#95a5a6'), padding: '2px 8px', fontSize: '12px' }}>✕</button>
                          </span>
                        ) : (
                          <span style={{ cursor: 'pointer' }} onClick={() => startEdit('list_type', detail?.list_type)}>
                            <TypeBadge type={detail?.list_type || activeList?.list_type} />
                            <span style={{ color: '#bbb', marginLeft: '4px' }}>✎</span>
                          </span>
                        )}
                      </div>

                      {/* Match field */}
                      <div>
                        <span style={{ color: '#888' }}>Field: </span>
                        {editField === 'match_field' ? (
                          <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'flex-start' }}>
                            <SearchableSelect
                              options={buildMatchFieldOptions(allLeadFields, detail?.match_field)}
                              value={editValue}
                              onChange={setEditValue}
                              placeholder="Search fields…"
                            />
                            <button onClick={saveEdit} style={{ ...btn('#27ae60'), padding: '2px 8px', fontSize: '12px' }}>✓</button>
                            <button onClick={() => setEditField(null)} style={{ ...btn('#95a5a6'), padding: '2px 8px', fontSize: '12px' }}>✕</button>
                          </span>
                        ) : (
                          <span style={{ fontFamily: 'monospace', background: '#f0f4f8', padding: '1px 6px', borderRadius: '3px', cursor: 'pointer' }}
                            onClick={() => startEdit('match_field', detail?.match_field)}>
                            {detail?.match_field || <em style={{ color: '#bbb', fontFamily: 'sans-serif' }}>not set</em>}
                            <span style={{ color: '#bbb', marginLeft: '4px', fontFamily: 'sans-serif' }}>✎</span>
                          </span>
                        )}
                      </div>

                      {/* Match type */}
                      <div>
                        <span style={{ color: '#888' }}>Match: </span>
                        {editField === 'match_type' ? (
                          <span style={{ display: 'inline-flex', gap: '4px' }}>
                            <select value={editValue} onChange={e => setEditValue(e.target.value)} style={{ ...inputStyle, padding: '2px 6px', fontSize: '12px' }}>
                              {Object.entries(MATCH_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <button onClick={saveEdit} style={{ ...btn('#27ae60'), padding: '2px 8px', fontSize: '12px' }}>✓</button>
                            <button onClick={() => setEditField(null)} style={{ ...btn('#95a5a6'), padding: '2px 8px', fontSize: '12px' }}>✕</button>
                          </span>
                        ) : (
                          <span style={{ cursor: 'pointer' }} onClick={() => startEdit('match_type', detail?.match_type || 'exact')}>
                            {MATCH_TYPES[detail?.match_type] || 'Exact match'}
                            <span style={{ color: '#bbb', marginLeft: '4px' }}>✎</span>
                          </span>
                        )}
                      </div>

                      {/* Client */}
                      <div>
                        <span style={{ color: '#888' }}>Client: </span>
                        {editField === 'client_id' ? (
                          <span style={{ display: 'inline-flex', gap: '4px' }}>
                            <select value={editValue} onChange={e => setEditValue(e.target.value)} style={{ ...inputStyle, padding: '2px 6px', fontSize: '12px' }}>
                              <option value="">None</option>
                              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <button onClick={saveEdit} style={{ ...btn('#27ae60'), padding: '2px 8px', fontSize: '12px' }}>✓</button>
                            <button onClick={() => setEditField(null)} style={{ ...btn('#95a5a6'), padding: '2px 8px', fontSize: '12px' }}>✕</button>
                          </span>
                        ) : (
                          <span style={{ cursor: 'pointer' }} onClick={() => startEdit('client_id', detail?.client_id || '')}>
                            {clients.find(c => c.id === detail?.client_id)?.name || <em style={{ color: '#bbb' }}>None</em>}
                            <span style={{ color: '#bbb', marginLeft: '4px' }}>✎</span>
                          </span>
                        )}
                      </div>

                      {detail?.created_at && (
                        <div><span style={{ color: '#888' }}>Created: </span>{new Date(detail.created_at + 'Z').toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => { setShowClone(true); setCloneName(`${detail?.list_name || ''} (copy)`); }} style={btn('#8e44ad')}>Clone</button>
                    <button onClick={() => handleDelete(activeId)} style={btn('#e74c3c')}>Delete</button>
                  </div>
                </div>

                {/* Clone form */}
                {showClone && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input value={cloneName} onChange={e => setCloneName(e.target.value)} placeholder="New list name" style={{ ...inputStyle, flex: 1 }} autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleClone(); if (e.key === 'Escape') setShowClone(false); }} />
                    <button onClick={handleClone} style={btn('#27ae60')}>Clone</button>
                    <button onClick={() => setShowClone(false)} style={btn('#95a5a6')}>Cancel</button>
                  </div>
                )}
              </div>

              {/* ── Items ── */}
              <Section title={`Items (${itemsTotal.toLocaleString()})`}>
                {/* Toolbar */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input placeholder="Search items…" value={itemSearchInput} onChange={e => handleItemSearchChange(e.target.value)}
                    style={{ ...inputStyle, flex: 1, minWidth: '160px' }} />
                  <select value={uploadMode} onChange={e => setUploadMode(e.target.value)} style={{ ...inputStyle, padding: '6px 8px' }}>
                    <option value="append">Append</option>
                    <option value="replace">Replace</option>
                  </select>
                  <button onClick={() => fileRef.current.click()} style={btn('#9b59b6')}>Upload CSV/XLSX</button>
                  <input type="file" accept=".csv,.xlsx" ref={fileRef} style={{ display: 'none' }} onChange={handleUpload} />
                  <button onClick={handleDownload} style={btn('#2980b9')}>⬇ Download</button>
                  {itemsTotal > 0 && <button onClick={handleClearAll} style={btn('#e74c3c')}>Clear All</button>}
                </div>

                {/* Table */}
                {items.length === 0 ? (
                  <div style={{ color: '#aaa', fontSize: '13px', padding: '12px 0' }}>
                    {itemSearch ? 'No items match your search.' : 'No items yet.'}
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e8ecef', borderRadius: '6px', overflow: 'hidden', marginBottom: '10px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      {detail?.columns && (
                        <thead>
                          <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e8ecef' }}>
                            {detail.columns.map(col => (
                              <th key={col} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: '600', color: '#555' }}>{col}</th>
                            ))}
                            <th style={{ width: '32px' }}></th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {items.map(item => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            {detail?.columns ? (
                              detail.columns.map(col => (
                                <td key={col} style={{ padding: '6px 12px', color: '#2c3e50' }}>{item.extra?.[col] ?? ''}</td>
                              ))
                            ) : (
                              <td style={{ padding: '6px 12px', color: '#2c3e50' }}>{item.value}</td>
                            )}
                            <td style={{ padding: '6px 8px', textAlign: 'right', width: '32px' }}>
                              <button onClick={() => handleDeleteItem(item.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: 0 }}>×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {pages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px' }}>
                    <button onClick={() => setItemPage(p => Math.max(1, p - 1))} disabled={itemPage === 1} style={{ ...btn('#e8ecef'), color: '#2c3e50', padding: '4px 10px' }}>‹ Prev</button>
                    <span style={{ color: '#555' }}>Page {itemPage} of {pages} ({itemsTotal.toLocaleString()} items)</span>
                    <button onClick={() => setItemPage(p => Math.min(pages, p + 1))} disabled={itemPage === pages} style={{ ...btn('#e8ecef'), color: '#2c3e50', padding: '4px 10px' }}>Next ›</button>
                  </div>
                )}

                {/* Add items */}
                <div>
                  <label style={labelStyle}>
                    {detail?.columns
                      ? `Add Rows — one per line, columns separated by tab or comma (${detail.columns.join(', ')})`
                      : 'Add Items (one per line)'}
                  </label>
                  <textarea value={addText} onChange={e => setAddText(e.target.value)}
                    style={{ ...inputStyle, width: '100%', height: '80px', resize: 'vertical', marginBottom: '8px' }}
                    placeholder={detail?.columns
                      ? detail.columns.map((c, i) => (i === 0 ? 'example-1' : `${c}-1`)).join(', ')
                      : 'value1\nvalue2\nvalue3'} />
                  <button onClick={handleAddItems} disabled={!addText.trim() || savingItems} style={btn('#27ae60')}>
                    {savingItems ? 'Saving…' : 'Add Items'}
                  </button>
                </div>
              </Section>

              {/* ── Assignments ── */}
              <Section title={`Assignments (${assignments.length})`} defaultOpen={false}>

                {/* Assignments table */}
                {assignments.length === 0 ? (
                  <div style={{ color: '#aaa', fontSize: '13px', marginBottom: '12px' }}>Not assigned to any goals yet.</div>
                ) : (
                  <div style={{ border: '1px solid #e8ecef', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e8ecef' }}>
                          <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: '600', color: '#555' }}>Program</th>
                          <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: '600', color: '#555' }}>Component</th>
                          <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: '600', color: '#555' }}>Goal</th>
                          <th style={{ padding: '7px 8px', textAlign: 'left', fontWeight: '600', color: '#555' }}>Since</th>
                          <th style={{ width: '32px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map(a => (
                          <tr key={`${a.type}-${a.id}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '6px 12px', color: '#2c3e50' }}>{a.program_name || '—'}</td>
                            <td style={{ padding: '6px 12px', color: '#555' }}>{a.component_name || '—'}</td>
                            <td style={{ padding: '6px 12px', color: '#555' }}>
                              {a.type === 'goal' ? (a.goal_name || '—') : <em style={{ color: '#aaa' }}>all goals</em>}
                            </td>
                            <td style={{ padding: '6px 8px', color: '#aaa', whiteSpace: 'nowrap' }}>
                              {a.assigned_at ? new Date(a.assigned_at + 'Z').toLocaleDateString() : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <button onClick={() => handleRemoveAssignment(a)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: 0 }}>×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add assignment — Program → Component → Goal cascade */}
                <div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Add Assignment</div>
                  {detail?.client_id && (
                    <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '8px' }}>
                      Showing programs for: <strong>{clients.find(c => c.id === detail.client_id)?.name}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                    {/* Step 1: Program */}
                    <div>
                      <label style={labelStyle}>1. Program</label>
                      <SearchableSelect value={assignProgram} onChange={setAssignProgram}
                        options={assignProgramOptionGroups.options} closedOptions={assignProgramOptionGroups.closedOptions}
                        placeholder="Select program…" />
                    </div>

                    {/* Step 2: Component */}
                    {assignProgram && (
                      <div>
                        <label style={labelStyle}>2. Component <span style={{ fontWeight: 400, color: '#888' }}>(or leave blank to assign all)</span></label>
                        <select value={assignComponent} onChange={e => setAssignComponent(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                          <option value="">All components / all goals in program</option>
                          {assignComponentOptions.map(c => <option key={c.id} value={c.id}>{c.component_name}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Step 3: Goals */}
                    {assignComponent && assignGoalOptions.length > 0 && (
                      <div>
                        <label style={labelStyle}>3. Goals <span style={{ fontWeight: 400, color: '#888' }}>(hold Ctrl/⌘ to multi-select; leave blank for all goals in component)</span></label>
                        <select multiple value={assignGoalIds.map(String)}
                          onChange={e => setAssignGoalIds([...e.target.selectedOptions].map(o => parseInt(o.value)))}
                          style={{ ...inputStyle, width: '100%', height: '100px' }}>
                          {assignGoalOptions.map(g => <option key={g.id} value={g.id}>{g.goal_name}</option>)}
                        </select>
                      </div>
                    )}

                    <div>
                      <button onClick={handleAssign} disabled={!assignProgram || assigning} style={btn('#27ae60')}>
                        {assignLabel()}
                      </button>
                    </div>
                  </div>
                </div>
              </Section>

              {/* ── List Types (admin only) ── */}
              {isAdmin && (
                <Section title="Manage List Types" defaultOpen={false}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
                    Click a type to apply it to this list. Use × to remove the type from the catalog.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                    {listTypes.map(t => {
                      const isActive = detail?.list_type === t.value;
                      return (
                        <div key={t.id} style={{ display: 'inline-flex', alignItems: 'center', border: `2px solid ${isActive ? typeColor(t.value) : '#e8ecef'}`, borderRadius: '6px', overflow: 'hidden' }}>
                          <button
                            onClick={async () => {
                              if (isActive) return;
                              const res = await axios.put(`${API}/targeting-lists/${activeId}`, { list_type: t.value });
                              setDetail(res.data);
                              fetchLists();
                            }}
                            title={isActive ? 'Current type' : `Set type to ${t.value}`}
                            style={{ background: isActive ? typeColor(t.value) : 'white', border: 'none', cursor: 'pointer', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: isActive ? 'white' : '#2c3e50' }}
                          >
                            {isActive && <span>✓</span>}
                            {t.value}
                          </button>
                          <button onClick={() => handleDeleteType(t.id, t.value)}
                            style={{ background: 'none', border: 'none', borderLeft: '1px solid #e8ecef', color: '#e74c3c', cursor: 'pointer', padding: '5px 7px', fontSize: '13px', lineHeight: 1 }}
                            title="Remove from catalog">×</button>
                        </div>
                      );
                    })}
                    {listTypes.length === 0 && <div style={{ color: '#aaa', fontSize: '13px' }}>No list types defined yet.</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input value={newTypeName} onChange={e => setNewTypeName(e.target.value)} placeholder="New type name (e.g. Intent Data)"
                      style={{ ...inputStyle, flex: 1 }}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddType(); }} />
                    <button onClick={handleAddType} disabled={!newTypeName.trim() || addingType} style={btn('#27ae60')}>
                      {addingType ? 'Adding…' : 'Add Type'}
                    </button>
                  </div>
                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '6px' }}>
                    Removing a type from the catalog does not affect existing lists that use it.
                  </div>
                </Section>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
